var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import { clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { canBeConvertedToPdf, formatPhoneNumber } from '@govinity/shared-config';
export function cn() {
    var inputs = [];
    for (var _i = 0; _i < arguments.length; _i++) {
        inputs[_i] = arguments[_i];
    }
    return twMerge(clsx(inputs));
}
/**
 * Formats a number of bytes into a human-readable string.
 *
 * @param bytes - The number of bytes to format
 * @param opts - Formatting options
 * @param [opts.decimals=0] - Number of decimal places to show
 * @param [opts.sizeType='normal'] - Use 'accurate' for binary prefixes (KiB, MiB) or 'normal' for decimal (KB, MB)
 * @returns A formatted string with appropriate size unit
 *
 * @example
 * ```ts
 * formatBytes(1234567, { decimals: 2 })
 * // Returns "1.23 MB"
 * ```
 *
 * @example
 * ```ts
 * formatBytes(1234567, { decimals: 2, sizeType: 'accurate' })
 * // Returns "1.18 MiB"
 * ```
 */
export function formatBytes(bytes, opts) {
    var _a, _b;
    if (opts === void 0) { opts = {}; }
    var _c = opts.decimals, decimals = _c === void 0 ? 0 : _c, _d = opts.sizeType, sizeType = _d === void 0 ? 'normal' : _d;
    var sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    var accurateSizes = ['Bytes', 'KiB', 'MiB', 'GiB', 'TiB'];
    if (bytes === 0)
        return '0 Byte';
    var i = Math.floor(Math.log(bytes) / Math.log(1024));
    return "".concat((bytes / Math.pow(1024, i)).toFixed(decimals), " ").concat(sizeType === 'accurate' ? ((_a = accurateSizes[i]) !== null && _a !== void 0 ? _a : 'Bytes') : ((_b = sizes[i]) !== null && _b !== void 0 ? _b : 'Bytes'));
}
export { formatPhoneNumber };
/**
 * Formats a phone number for tel: links with country code and number only
 * @param phone - Phone data object with countryCode, number, and extension
 * @returns Formatted phone number string in +${countryCode}${number} format or null if no number
 *
 * @example
 * ```ts
 * formatPhoneNumberForTel({ countryCode: '1', number: '1234567890', extension: '123' })
 * // Returns "+11234567890"
 * ```
 *
 * @example
 * ```ts
 * formatPhoneNumberForTel({ countryCode: '44', number: '1234567890', extension: null })
 * // Returns "+441234567890"
 * ```
 */
export function formatPhoneNumberForTel(phone) {
    if (!phone || !phone.number) {
        return null;
    }
    var countryCode = phone.countryCode, number = phone.number;
    return "+".concat(countryCode).concat(number);
}
/**
 * Converts a CSS value (rem or px) to pixels.
 *
 * @param value - CSS value string (e.g., "24rem", "640px", or "640")
 * @returns The pixel value as a number
 *
 * @example
 * ```ts
 * convertCssToPixels("24rem")
 * // Returns 384 (assuming 16px root font size)
 * ```
 *
 * @example
 * ```ts
 * convertCssToPixels("640px")
 * // Returns 640
 * ```
 */
export function convertCssToPixels(value) {
    if (value.endsWith('rem')) {
        var remValue = parseFloat(value);
        var rootFontSize = parseFloat(getComputedStyle(document.documentElement).fontSize);
        return remValue * rootFontSize;
    }
    if (value.endsWith('px')) {
        return parseFloat(value);
    }
    // Fallback: try to parse as number (assumes px)
    return parseFloat(value);
}
/**
 * Fetches a document from its signed URL as a same-origin blob URL. The signed URL
 * points at cross-origin storage, so blobbing the response first is what lets callers
 * force a download filename or hand the file to a same-origin iframe for printing.
 *
 * @throws if the URL cannot be fetched (e.g. an expired or forbidden signed URL), rather
 * than blobbing an error body that would then be saved or printed as if it were the document.
 */
export function fetchDocumentBlobUrl(signedUrl) {
    return __awaiter(this, void 0, void 0, function () {
        var response, blob;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, fetch(signedUrl)];
                case 1:
                    response = _a.sent();
                    if (!response.ok) {
                        throw new Error("Failed to fetch document (".concat(response.status, ")"));
                    }
                    return [4 /*yield*/, response.blob()];
                case 2:
                    blob = _a.sent();
                    return [2 /*return*/, URL.createObjectURL(blob)];
            }
        });
    });
}
/**
 * Downloads a document under its original filename. The anchor `download` attribute alone
 * is ignored for cross-origin signed URLs, so the response is fetched into a same-origin
 * blob URL first, which is then downloaded and revoked.
 *
 * @throws if the signed URL cannot be fetched (e.g. expired or forbidden).
 */
export function downloadDocument(signedUrl, filename) {
    return __awaiter(this, void 0, void 0, function () {
        var objectUrl;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, fetchDocumentBlobUrl(signedUrl)];
                case 1:
                    objectUrl = _a.sent();
                    downloadObjectUrl(objectUrl, filename);
                    return [2 /*return*/];
            }
        });
    });
}
export function withFileExtension(name, extension) {
    var _a;
    var _b = (_a = name.match(/^(.+)\.([^.]+)$/)) !== null && _a !== void 0 ? _a : [], baseName = _b[1], currentExtension = _b[2];
    var normalizedExtension = currentExtension === null || currentExtension === void 0 ? void 0 : currentExtension.toLowerCase();
    if (normalizedExtension === extension.toLowerCase()) {
        return name;
    }
    // Converted PDFs keep their source's name (e.g. "Report.docx"), so the source extension is swapped
    if (extension.toLowerCase() === 'pdf' &&
        normalizedExtension &&
        canBeConvertedToPdf(normalizedExtension)) {
        return "".concat(baseName, ".").concat(extension);
    }
    return "".concat(name, ".").concat(extension);
}
export function downloadBlob(blob, filename) {
    downloadObjectUrl(URL.createObjectURL(blob), filename);
}
function downloadObjectUrl(objectUrl, filename) {
    var link = document.createElement('a');
    link.href = objectUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(objectUrl);
}
